<template>
    <div class="section-container">
        <!-- 分栏导航区 -->
        <div class="section-nav-wrapper">
            <draggable
                v-model="sectionsList"
                @end="onDragEnd"
                handle=".drag-handle"
                tag="div"
                class="section-tabs"
                item-key="sectionId"
            >
                <template #item="{ element: section }">
                    <div
                        :class="['section-tab', { active: activeSectionId === section.sectionId }]"
                        @click="setActiveSection(section.sectionId)"
                    >
                        <span class="drag-handle section-name">
                            {{ section.sectionName }}
                        </span>
                        <div v-if="activeSectionId === section.sectionId" class="active-indicator"></div>
                    </div>
                </template>
            </draggable>
        </div>

        <!-- 内容展示区 -->
        <div class="section-body">
            <transition name="fade" mode="out-in">
                <SiteList
                    v-if="activeSection"
                    :key="activeSection.sectionId"
                    :sites="activeSection.sites || []"
                    :sectionId="activeSection.sectionId"
                />
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps } from 'vue';
import { useStore } from 'vuex';
import { Message } from '@arco-design/web-vue';
import draggable from 'vuedraggable';
import SiteList from './SiteList.vue';

const props = defineProps({
    sections: {
        type: Array,
        required: true,
        default: () => [],
    },
    orgId: {
        type: Number,
        required: true,
    },
});

const store = useStore();
const sectionsList = ref([...props.sections]);
const activeSectionId = ref(null);

const activeSection = computed(() => {
    return sectionsList.value.find(section => section.sectionId === activeSectionId.value);
});

watch(() => props.sections, (newSections) => {
    sectionsList.value = [...newSections];
    if (!sectionsList.value.find(section => section.sectionId === activeSectionId.value)) {
        activeSectionId.value = sectionsList.value.length > 0 ? sectionsList.value[0].sectionId : null;
    }
}, { deep: true, immediate: true });

const reorderSections = (data) => store.dispatch('sections/reorderSections', data);

const setActiveSection = (sectionId) => {
    activeSectionId.value = sectionId;
};

const onDragEnd = async () => {
    const reorderedSections = sectionsList.value.map((section, index) => ({
        ...section,
        sortRank: index + 1,
    }));
    try {
        await reorderSections({ orgId: props.orgId, sections: reorderedSections });
        Message.success('分栏排序已更新');
    } catch (error) {
        console.error('分栏排序更新失败:', error);
        Message.error('分栏排序更新失败');
    }
};

onMounted(() => {
    if (sectionsList.value.length > 0) {
        activeSectionId.value = sectionsList.value[0].sectionId;
    }
});
</script>

<style scoped>
.section-container {
    width: 100%;
}

.section-nav-wrapper {
    margin-bottom: 12px; /* 减小下方间距 */
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding-bottom: 2px;
}

.section-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 4px; /* 从 8px 减小到 4px */
}

.section-tab {
    position: relative;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
    font-size: 0.95rem;
    background: transparent;
}

.section-tab:hover {
    color: #165DFF;
    background: rgba(22, 93, 255, 0.05);
}

.section-tab.active {
    color: #165DFF;
    font-weight: 600;
    background: rgba(22, 93, 255, 0.08);
}

.active-indicator {
    /* 如果需要底部横条样式，可以在这里添加，目前使用胶囊背景 */
    /*
    position: absolute;
    bottom: -2px;
    left: 10px;
    right: 10px;
    height: 2px;
    background-color: #165DFF;
    border-radius: 2px;
    */
}

.section-name {
    cursor: grab;
    user-select: none;
}

.section-body {
    min-height: 100px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
